import React, { useCallback } from 'react';

/**
 * Dual-handle range slider for setting a comfort zone (min + max on a 1–10 scale).
 *
 * Props:
 *   min       {number}   current minimum value
 *   max       {number}   current maximum value
 *   onChange  {function} called with ({ min, max }) on any change
 *   color     {string}   hex colour for the filled track segment
 *   label     {string}   accessible label
 */
export default function DualRangeSlider({ min, max, onChange, color = '#C4714A', label }) {
  const TRACK_MIN = 1;
  const TRACK_MAX = 10;

  const handleMinChange = useCallback((e) => {
    const newMin = Math.min(Number(e.target.value), max - 1);
    onChange({ min: newMin, max });
  }, [max, onChange]);

  const handleMaxChange = useCallback((e) => {
    const newMax = Math.max(Number(e.target.value), min + 1);
    onChange({ min, max: newMax });
  }, [min, onChange]);

  // Percentage positions for the filled track gradient
  const minPct = ((min - TRACK_MIN) / (TRACK_MAX - TRACK_MIN)) * 100;
  const maxPct = ((max - TRACK_MIN) / (TRACK_MAX - TRACK_MIN)) * 100;

  const trackStyle = {
    background: `linear-gradient(
      to right,
      #e5e7eb ${minPct}%,
      ${color} ${minPct}%,
      ${color} ${maxPct}%,
      #e5e7eb ${maxPct}%
    )`,
  };

  return (
    <div className="w-full" aria-label={label}>
      {/* Scale labels */}
      <div className="flex justify-between text-xs text-gray-400 mb-1 px-0.5">
        {[1,2,3,4,5,6,7,8,9,10].map(n => (
          <span key={n} className={`${(n === min || n === max) ? 'font-bold text-gray-700' : ''}`}>
            {n}
          </span>
        ))}
      </div>

      {/* Slider track */}
      <div className="relative h-6 flex items-center">
        <div
          className="absolute w-full h-2 rounded-full"
          style={trackStyle}
        />

        {/* Min handle */}
        <input
          type="range"
          min={TRACK_MIN}
          max={TRACK_MAX}
          step={1}
          value={min}
          onChange={handleMinChange}
          className="absolute w-full h-2 appearance-none bg-transparent cursor-pointer"
          style={{ WebkitAppearance: 'none' }}
          aria-label={`${label} minimum`}
        />

        {/* Max handle */}
        <input
          type="range"
          min={TRACK_MIN}
          max={TRACK_MAX}
          step={1}
          value={max}
          onChange={handleMaxChange}
          className="absolute w-full h-2 appearance-none bg-transparent cursor-pointer"
          style={{ WebkitAppearance: 'none' }}
          aria-label={`${label} maximum`}
        />
      </div>

      {/* Current range display */}
      <div className="flex justify-between mt-2 text-sm">
        <span className="text-gray-500">
          Low end: <span className="font-semibold text-gray-700">{min}</span>
        </span>
        <span className="font-medium" style={{ color }}>
          Comfort zone: {min}–{max}
        </span>
        <span className="text-gray-500">
          High end: <span className="font-semibold text-gray-700">{max}</span>
        </span>
      </div>

      {/* Slider thumb styles injected globally — only needed once */}
      <style>{`
        input[type='range']::-webkit-slider-thumb {
          -webkit-appearance: none;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: white;
          border: 2px solid ${color};
          cursor: pointer;
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
          pointer-events: all;
        }
        input[type='range']::-moz-range-thumb {
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: white;
          border: 2px solid ${color};
          cursor: pointer;
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        }
      `}</style>
    </div>
  );
}
